// Portfolio data. Edit this file to change all content shown on the site.
export const portfolio = {
  meta: {
    name: "Akshay Sawant",
    nameSplit: { first: "Akshay", last: "Sawant" },
    title: "Full-Stack Developer",
    location: "Mumbai / Ahmedabad, India",
    coords: "23.0225° N · 72.5714° E",
    volume: "Vol. 05 · '26",
    issue: "Feb '26",
    email: "asawant8334@gmail.com",
    phone: "+91 90043 73416",
    linkedin: "https://linkedin.com/in/akshaysawant8334",
    tagline:
      "I build conversion-focused websites and AI-powered tools for international clients — WordPress, Shopify, Webflow, and Wix, from Figma to launch.",
    status: "Full stack developer",
    heroVideo: "https://customer-assets.emergentagent.com/job_portfolio-stage-75/artifacts/3lk88gyb_Slow_motion_zoom_on_person_202606201423.mp4",
    heroPoster: "https://images.unsplash.com/photo-1604871000636-074fa5117945?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80",
  },
  selectedProof: [
    { value: "6+", suffix: "years", label: "Full stack developer" },
    { value: "30+", suffix: "sites shipped", label: "for international clients" },
    { value: "90%+", suffix: "", label: "PageSpeed improvements" },
  ],
  proofLine:
    "Six years building scalable, conversion-focused digital products for international clients — from pixel-perfect Figma-to-UI implementation to custom WordPress plugins, Shopify storefronts, REST API integrations, and AI-assisted automation tooling.",
  now: [
    { kind: "Building", title: "AI Workflow Tools", detail: "Custom automation saving clients 10+ hrs/week" },
    { kind: "Leading", title: "Team of 4 Developers", detail: "Iflair · Code review + delivery" },
    { kind: "Shipping", title: "Conversion Storefronts", detail: "Shopify · Webflow · WordPress" },
    { kind: "Open to", title: "Senior + Lead Roles", detail: "Remote-first · 2026" },
  ],
  disciplines: [
    "WordPress",
    "Shopify",
    "Webflow",
    "Wix",
    "Custom themes & plugins",
    "Figma-to-UI",
    "REST API integrations",
    "AI workflow automation",
    "Custom admin dashboards",
    "Core Web Vitals",
    "Team leadership",
    "International client delivery",
  ],
  background: {
    section: "01",
    eyebrow: "Background & Working Style",
    heading: "A developer who thinks in conversions and systems",
    paragraphs: [
      "I build the websites and tools businesses count on every day: storefronts that convert, custom dashboards that surface real data, and AI automations that quietly reclaim hours of manual work. I care about the pixel users see and the schema underneath.",
      "I have five years of full-stack web experience across WordPress, Shopify, Webflow, and Wix — most of it shipping for international clients across e-commerce, SaaS, and service industries. Outside the CMS world, I work with PHP, MySQL, REST APIs, and the growing stack of LLM-powered tools that now sit inside almost every brief.",
      "I do my best work when a client needs someone to own the full arc: gather the requirements, push the Figma into production, integrate the third-party pieces, and still care about the Core Web Vitals on the day of launch. These days, that increasingly includes custom AI tooling — automating the workflows clients used to do by hand.",
    ],
  },
  timeline: [
    { year: "'21", label: "B.E. Computer Engineering", place: "Mumbai University", image: "/timeline/timeline_education.png" },
    { year: "'21", label: "Web Developer · Exellartech", place: "Figma → WordPress", image: "/timeline/timeline_web_dev.png" },
    { year: "'23", label: "Full-Stack Developer · Iflair", place: "International client delivery", image: "/timeline/timeline_fullstack.png" },
    { year: "'25", label: "Team Management", place: "Up to 4 developers · Iflair", image: "/timeline/timeline_management.png" },
    { year: "'25", label: "AI-assisted client tooling", place: "LLM integrations · Automation", image: "/timeline/timeline_ai.png" },
    { year: "'26", label: "Open to senior + team management", place: "Remote-first", image: "/timeline/timeline_remote.png" },
  ],
  timelineFootnotes: [
    "Six years production web",
    "Two companies · one trajectory",
  ],
  studies: {
    section: "02",
    eyebrow: "How I Think and Build",
    heading: "Two studies in vision and action",
    intro:
      "Two short visual studies for the site, in the same editorial register I bring to client briefs: keep the imagery considered, keep the typography honest, and keep the page light enough to score well in the moments that decide whether a visitor stays.",
    items: [
      {
        code: "A.01",
        cap: "FOCUS",
        capRight: "Mumbai / Ahmedabad, IN",
        meta1: "OPEN // SENIOR ROLES",
        meta2: "23.0225° N · 72.5714° E",
        title: "Slow Zoom on the Detail",
        body: "The work that gets noticed is usually the work nobody asked for: the loading state, the empty state, the keyboard nudge. The brief is to ship; the craft is to keep looking.",
        image: "/studies/slow-zoom-detail.png",
        alt: "Editorial study showing detailed UI craft, loading states, and keyboard interaction polish",
      },
      {
        code: "A.02",
        cap: "CONVERSION",
        capRight: "30+ // SITES SHIPPED",
        meta1: "05 YRS // FULL-STACK WEB",
        meta2: "VOL // 05 · 2026",
        title: "The Page That Converts",
        body: "A conversion-focused build is not a louder page — it is a quieter, faster, more obvious one. Every project I ship gets the same final pass: PageSpeed, accessibility, copy, and the single button you came here to press.",
        image: "/studies/page-that-converts.png",
        alt: "Editorial study showing a conversion-focused landing page with performance and accessibility signals",
      },
    ],
  },
  byTheWork: [
    { value: "30+", unit: "sites", label: "shipped for international clients" },
    { value: "90%+", unit: "", label: "PageSpeed score improvement" },
    { value: "06", unit: "years", label: "Full stack developer" },
    { value: "10+", unit: "hrs / week", label: "saved via AI automation" },
    { value: "50%", unit: "", label: "reduction in average load time" },
    { value: "04", unit: "devs led", label: "team manage at Iflair" },
  ],
  experience: {
    section: "03",
    eyebrow: "Engineering Experience",
    heading: "Six years shipping for international clients.",
    roles: [
      {
        roman: "I",
        start: "2023",
        end: "Present",
        kicker: "International client delivery · Team lead",
        title: "Full-Stack WordPress Developer",
        company: "Iflair Web Technology",
        summary:
          "Lead full-stack delivery for international clients across e-commerce, SaaS, and service industries. Own the full arc — custom WordPress themes and plugins, Shopify / Webflow / Wix builds, third-party integrations, AI tooling, performance — while leading a team of up to four developers through code review and on-time delivery.",
        bullets: [
          {
            title: "30+ International Sites",
            lede: "Delivered high-performance WordPress, Shopify, Webflow, and Wix builds for clients across e-commerce, SaaS, and services.",
            stat: "30+",
            statLabel: "sites shipped",
            tail: "Consistent 5-star client feedback and a 100% on-time delivery rate across the engagement.",
          },
          {
            title: "AI Automation Tools",
            lede: "Built custom AI-powered automations and admin tools that replaced manual client workflows.",
            stat: "10+ hrs",
            statLabel: "reclaimed / week",
            tail: "LLM-assisted internal tooling integrated directly into client CMS environments, with clear handoff to non-technical teams.",
          },
          {
            title: "Core Web Vitals",
            lede: "Caching, CDN configuration, image pipelines, and code-level optimization to land LCP / FID / CLS in the green.",
            stat: "90%+",
            statLabel: "PageSpeed lift",
            tail: "Performance audits with Lighthouse and GTmetrix, applied to live international stores.",
          },
          {
            title: "Custom Themes, Plugins & Dashboards",
            lede: "ACF-backed dynamic data, custom post types, PHP-driven admin dashboards for real-time business reporting.",
            stat: "",
            statLabel: "",
            tail: "Reduced client dependency on bloated third-party plugins by replacing them with focused, owned code.",
          },
        ],
      },
      {
        roman: "II",
        start: "2021",
        end: "2023",
        kicker: "Figma → Pixel-perfect WordPress",
        title: "Web Developer",
        company: "Exellartech",
        summary:
          "Converted design files into pixel-perfect, fully responsive WordPress sites. Customized the major page-builder ecosystems (Elementor Pro, WPBakery, Divi) for client-specific needs and ran performance audits that became the team standard.",
        bullets: [
          {
            title: "Figma → Responsive WP",
            lede: "Translated Figma and Adobe XD designs into pixel-perfect, fully responsive WordPress sites.",
            stat: "",
            statLabel: "",
            tail: "Set the template for the design-handoff workflow used across the team.",
          },
          {
            title: "Performance Audits",
            lede: "Used GTmetrix, PageSpeed Insights, and Lighthouse to find and fix front-end bottlenecks.",
            stat: "50%",
            statLabel: "faster loads",
            tail: "Reduced average load time across client sites by up to 50%.",
          },
          {
            title: "Dynamic, Data-Driven Pages",
            lede: "ACF + custom post types to give marketing teams editable templates without touching code.",
            stat: "",
            statLabel: "",
            tail: "Debugged complex frontend and backend issues using DevTools and server-side error logging.",
          },
        ],
      },
    ],
  },
  products: {
    section: "04",
    eyebrow: "Specialty Practices",
    heading: "Four practices, all mine, all shipping.",
    intro:
      "Four practices I bring to every client engagement. Each set in its own brand voice — colors, type, and tone independent of the surrounding magazine — because the work itself runs in four very different rooms.",
    plates: [
      {
        roman: "I",
        plate: "B.01",
        kicker: "Custom CMS Builds",
        name: "WordPress Studio",
        wordmark: "wordpress/",
        status: "Active · client delivery",
        quote: "Custom themes and plugins, no template debt.",
        body: "Custom WordPress themes, plugins, and dashboards built on ACF, custom post types, and PHP. The brief is usually: replace three bloated plugins with one piece of focused code, and surface real business data inside wp-admin.",
        tags: ["Custom themes", "ACF", "PHP", "MySQL", "Admin dashboards"],
        accent: "#1e293b",
        url: "https://mirto.be/",
        image: "/products/mirto.png",
      },
      {
        roman: "II",
        plate: "B.02",
        kicker: "E-commerce",
        name: "Shopify Storefronts",
        wordmark: "shopify/",
        status: "Active · conversion focus",
        quote: "Storefronts tuned for the single button you came to press.",
        body: "Shopify builds with custom UI, mobile-first performance, and a final pass on the moments that actually decide checkout — PDP, cart, and confirmation. Where the brief calls for it, I extend with apps and theme-level Liquid.",
        tags: ["Shopify", "Liquid", "Theme dev", "Conversion", "Mobile-first"],
        accent: "#065f46",
        url: "#",
      },
      {
        roman: "III",
        plate: "B.03",
        kicker: "Design Systems → Web",
        name: "Webflow & Wix",
        wordmark: "webflow/",
        status: "Active · brand & marketing",
        quote: "Pixel-perfect Figma in a fraction of the build time.",
        body: "Webflow and Wix builds for brand sites, marketing pages, and lightweight SaaS frontends. Strong CMS modeling, careful interaction work, and clean handoff so marketing teams can keep shipping after launch.",
        tags: ["Webflow", "Wix", "Figma-to-UI", "CMS modeling", "Interactions"],
        accent: "#b45309",
        url: "https://bachmannglobal.com/",
        image: "/products/bachmann.png",
      },
      {
        roman: "IV",
        plate: "B.04",
        kicker: "AI Web Apps & SaaS",
        name: "AI Solutions & Dashboards",
        wordmark: "ai-solutions/",
        status: "Active · 10+ hrs/wk reclaimed",
        quote: "Custom AI web apps, prompt engineering, and SaaS dashboards to automate and scale.",
        body: "Building custom web applications, SaaS dashboards, and automated tools powered by LLMs. I design robust prompt engineering pipelines and custom AI solutions to solve complex business problems, providing clients with intuitive admin panels to run their automated workflows.",
        tags: ["AI Web Apps", "SaaS Dashboards", "Prompt Engineering", "AI Solutions", "Workflow Automation", "LLM APIs"],
        accent: "#7c3aed",
        url: "https://q-designer.q-connect.com/",
        image: "/products/qconnect.png",
      },
    ],
  },
  toolkit: {
    section: "05",
    eyebrow: "Technical Toolkit",
    heading: "The stack under the hood.",
    intro: "Six working disciplines, carried from first Figma frame to the moment a site goes live.",
    groups: [
      {
        code: "T.01",
        stars: 10,
        title: "CMS & E-commerce",
        items: ["WordPress", "Shopify", "Webflow", "Wix", "WooCommerce", "Custom themes & plugins", "Page builders (Elementor Pro, WPBakery, Divi)"],
      },
      {
        code: "T.02",
        stars: 9,
        title: "Frontend",
        items: ["HTML5", "CSS3", "JavaScript", "Bootstrap", "Responsive UI", "Figma → Production", "Cross-browser polish"],
      },
      {
        code: "T.03",
        stars: 8,
        title: "Backend & APIs",
        items: ["PHP", "MySQL", "REST API design & integration", "Database architecture & optimization", "Custom admin dashboards", "ACF · Custom post types"],
      },
      {
        code: "T.04",
        stars: 9,
        title: "AI & Automation",
        items: ["LLM integrations", "Workflow automation", "Custom AI tooling", "Content & lead automations", "Non-technical UX for AI"],
      },
      {
        code: "T.05",
        stars: 9,
        title: "Performance & SEO",
        items: ["Core Web Vitals (LCP · FID · CLS)", "Caching strategy", "CDN configuration", "Image optimization", "Lighthouse · GTmetrix · PageSpeed Insights", "Technical SEO"],
      },
      {
        code: "T.06",
        stars: 8,
        title: "Practice",
        items: ["International client communication", "Team leadership (up to 4 devs)", "Code review", "Technical consultation", "Requirements gathering", "Project management"],
      },
    ],
  },
  currently: [
    "Full stack developer",
    "Full-Stack",
    "Mumbai / Ahmedabad, India",
    "Remote-first",
    "23.0225° N · 72.5714° E",
    "2026, accepting inquiries",
  ],
  contact: {
    section: "06",
    eyebrow: "Contact & Availability",
    heading: "Let's build something good.",
    body:
      "Open to senior full-stack and lead roles where craft, ownership, and conversion all matter. Available remote-first from Mumbai / Ahmedabad, starting in 2026. Reply within 24h.",
    inquiryTypes: ["Senior role", "Contract", "Consulting", "Other"],
  },
};
